import get from 'lodash/get';
import flowRight from 'lodash/flowRight';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'clsx';
import Drawer from 'material-ui/Drawer';
import { Link } from 'react-router-dom';
import getUserDataUrl from 'src/common/utils/paths/getUserDataUrl';
import { IHEART_GUIDE_ID } from 'src/common/constants/affiliates/iHeartRadio';
import { SEARCH_CAPITALIZED } from 'src/common/constants/localizations/search';
import {
  SEARCH_PATH,
  ACCOUNT_PATH,
  ROOT_PATH,
  PAYMENT_COMPLETE_PATH,
} from 'src/common/constants/paths';
import { SIGN_IN, SIGN_UP } from 'src/common/constants/localizations/auth';
import {
  LOGOUT,
  SETTINGS,
  LIBRARY,
} from 'src/common/constants/localizations/header';
import {
  selectIsExpiredWebSubscriber,
  selectIsUserSubscribed,
  selectUserDetails,
} from 'src/common/selectors/me';
import { selectShouldShowDesktopUpdateBanner } from 'src/common/selectors/desktop';
import {
  selectIsDesktop,
  selectTopBannerHeight,
} from 'src/common/selectors/app';
import connectWithAuth from '../../decorators/auth/connectWithAuth';
import getLoginLocationDescriptor from '../../utils/auth/getLoginLocationDescriptor';
import viewTypes from '../../constants/auth/viewTypes';
import { labels } from '../../constants/analytics/categoryActionLabel/subscribe';
import cssVariables from '../../styles/variables';
import getSubscribeEventLabel from '../../utils/subscription/getSubscribeEventLabel';
import { isLarge } from '../../utils/breakpoints';
import getTopBannerOffsetStyles from '../../utils/banner/getTopBannerOffsetStyles';
import { logWebActivity } from '../../actions/logging';
import { updateAuthViewType } from '../../actions/auth';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import debounce from '../../utils/debounce';

import SearchIcon from '../shared/svgIcons/SearchIcon';
import TuneInMarquee from '../shared/svgIcons/TuneInMarquee';
import UpsellButton from '../subscription/UpsellButton';
import Scrollbar from '../Scrollbar';
import css from './leftSide.module.scss';
import SidebarDesktopDownloadLink from './SidebarDesktopDownloadLink';
import NavigationMenu from './NavigationMenu';

const drawerStyle = {
  top: 0,
  backgroundColor: cssVariables['--secondary-color-5'],
  boxShadow: 'none',
  zIndex: cssVariables['--app-z-index'],
  overflow: 'hidden',
};
const overlayStyle = {
  cursor: 'pointer',
  backgroundColor: 'rgba(11, 14, 36, 0.5)',
  zIndex: cssVariables['--app-z-index'],
};
const leftSideWith = cssVariables['--leftSide-width'];

export class LeftSidebar extends Component {
  static propTypes = {
    userLocation: PropTypes.string,
    userDetails: PropTypes.object,
    isLeftSideOpen: PropTypes.bool,
    isLeftSideDocked: PropTypes.bool,
    iHeartGuideId: PropTypes.string.isRequired,
    handleLeftSideToggle: PropTypes.func,
    breakpoint: PropTypes.number.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isDesktop: PropTypes.bool.isRequired,
    currentGuideId: PropTypes.string,
    subscriptionEnabled: PropTypes.bool.isRequired,
    authActions: PropTypes.shape({
      logout: PropTypes.func.isRequired,
      needsSignInDialog: PropTypes.func.isRequired,
      needsSignUpDialog: PropTypes.func.isRequired,
    }),
    actions: PropTypes.shape({
      logWebActivity: PropTypes.func.isRequired,
      updateAuthViewType: PropTypes.func.isRequired,
    }),
    history: PropTypes.object.isRequired,
    isUserSubscribed: PropTypes.bool.isRequired,
    isExpiredWebSubscriber: PropTypes.bool.isRequired,
    shouldShowDesktopUpdateBanner: PropTypes.bool,
    topBannerHeight: PropTypes.number,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props, context) {
    super(props, context);
    this.logOut = debounce(this.logOut.bind(this));
    this.authLink = debounce(this.authLink.bind(this));
  }

  logOut() {
    const { authActions, history } = this.props;
    const { location } = this.context;
    authActions.logout();
    if (location.pathname === PAYMENT_COMPLETE_PATH) {
      history.push(ROOT_PATH);
    }
  }

  authLink(vt) {
    const { location } = this.context;
    const { actions, history } = this.props;
    actions.updateAuthViewType(vt);
    history.push(getLoginLocationDescriptor(location, vt));
  }

  render() {
    const {
      isDesktop,
      subscriptionEnabled,
      isUserSubscribed,
      isExpiredWebSubscriber,
      userDetails,
      userLocation,
      iHeartGuideId,
      isLeftSideOpen,
      isLeftSideDocked,
      handleLeftSideToggle,
      authActions,
      isAuthenticated,
      breakpoint,
      currentGuideId,
      shouldShowDesktopUpdateBanner,
      topBannerHeight,
    } = this.props;
    const { getLocalizedText, location } = this.context;
    let authItems;
    const userDataUrl = userDetails && getUserDataUrl(userDetails);

    if (isAuthenticated) {
      authItems = [
        <Link
          data-testid="favorites"
          className={classNames(css.navigationMenuItem, {
            [css.activeMenuItem]: location.pathname === userDataUrl,
          })}
          to={userDataUrl}
          children={getLocalizedText(LIBRARY)}
          key={1}
        />,
        <Link
          className={classNames(css.navigationMenuItem, {
            [css.activeMenuItem]: location.pathname === ACCOUNT_PATH,
          })}
          data-testid="account"
          to={ACCOUNT_PATH}
          children={getLocalizedText(SETTINGS)}
          key={2}
        />,
        <div
          data-testid="logout"
          className={css.navigationMenuItem}
          onClick={this.logOut}
          children={getLocalizedText(LOGOUT)}
          key={3}
        />,
      ];
    } else {
      const signInProps = {
        'data-testid': 'sidebarLogin',
        className: css.navigationMenuItem,
        children: getLocalizedText(SIGN_IN),
      };
      const signUpProps = {
        'data-testid': 'sidebarSignUp',
        className: css.navigationMenuItem,
        children: getLocalizedText(SIGN_UP),
      };

      if (isLarge(breakpoint) && !isDesktop) {
        authItems = [
          <div
            {...signInProps}
            onClick={authActions.needsSignInDialog}
            key={1}
          />,
          <div
            {...signUpProps}
            onClick={authActions.needsSignUpDialog}
            key={2}
          />,
        ];
      } else {
        authItems = [
          <a
            {...signInProps}
            // create an internal function to kick off action as well to change
            onClick={() => this.authLink(viewTypes.signIn)}
            key={1}
          />,
          <a
            {...signUpProps}
            onClick={() => this.authLink(viewTypes.signUp)}
            key={2}
          />,
        ];
      }
    }

    if (shouldShowDesktopUpdateBanner) {
      Object.assign(drawerStyle, getTopBannerOffsetStyles(topBannerHeight));
    }

    const eventLabel = getSubscribeEventLabel(
      labels.leftsidenavUpsellDialog,
      location,
    );
    const showUpsell = subscriptionEnabled && !isUserSubscribed;
    const upsellButtonContainer = (
      <div
        id="sidebarUpsellButtonContainer"
        data-testid="sidebarUpsellButtonContainer"
        className={css.upsellButtonContainer}
      >
        <UpsellButton
          dataTestId="sidebarUpsellLink"
          buttonClassName={css.upsellButton}
          isExpiredWebSubscriber={isExpiredWebSubscriber}
          source={eventLabel}
          guideId={currentGuideId}
        />
      </div>
    );

    return (
      <Drawer
        width={leftSideWith}
        docked={isLeftSideDocked}
        open={isLeftSideOpen}
        onRequestChange={handleLeftSideToggle}
        containerStyle={drawerStyle}
        overlayStyle={overlayStyle}
      >
        <Scrollbar width={leftSideWith}>
          <nav
            id="sidebarNav"
            data-testid="sidebarNav"
            className={classNames(css.nav, {
              [css.isDesktop]: isDesktop,
            })}
          >
            {!isDesktop && (
              <Link id="tuneInLogo" to="/" className={css.marqueeContainer}>
                <TuneInMarquee
                  width="112px"
                  height="50px"
                  fill={cssVariables['--t-sharp']}
                />
              </Link>
            )}
            {showUpsell && upsellButtonContainer}
            <Link
              id="searchsidebar"
              data-testid="search"
              className={classNames(
                css.navigationMenuItem,
                css.searchMenuItem,
                {
                  [css.activeMenuItem]: location.pathname === SEARCH_PATH,
                },
              )}
              to={SEARCH_PATH}
            >
              {getLocalizedText(SEARCH_CAPITALIZED)}
              <div
                id="searchIconContainer"
                data-testid="searchIconContainer"
                className={css.searchIconContainer}
              >
                <SearchIcon fill="#FFF" width="16px" height="16px" />
              </div>
            </Link>
            <NavigationMenu
              iHeartGuideId={iHeartGuideId}
              selectedGuideId={currentGuideId}
              subscriptionEnabled={subscriptionEnabled}
              userLocation={userLocation}
            />
            <div
              id="bottomAuth"
              data-testid="bottomAuth"
              className={css.authContainer}
              children={authItems}
            />
            <SidebarDesktopDownloadLink />
          </nav>
        </Scrollbar>
      </Drawer>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        logWebActivity,
        updateAuthViewType,
      },
      dispatch,
    ),
  };
}

function mapStateToProps(state) {
  const { config } = state;

  return {
    userLocation: get(config, ['location', 'country_code']),
    iHeartGuideId: get(config, ['autoupdate', IHEART_GUIDE_ID]),
    isDesktop: selectIsDesktop(state),
    userDetails: selectUserDetails(state),
    isUserSubscribed: selectIsUserSubscribed(state),
    isExpiredWebSubscriber: selectIsExpiredWebSubscriber(state),
    shouldShowDesktopUpdateBanner: selectShouldShowDesktopUpdateBanner(state),
    topBannerHeight: selectTopBannerHeight(state),
  };
}

export default flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  connectWithAuth,
)(LeftSidebar);
